'use strict';

/**
 * Community -- the team hub: a grid of everyone on the team, each a card you click to visit their
 * profile page. Data comes from the public Team/Roster endpoint (any signed-in teammate, public
 * profile fields only). Clicking a card navigates to /profile/:id.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libAvatarHelper = require('./AvatarHelper.js');

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

class PictViewCommunity extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.pict.AppData.Community = this.pict.AppData.Community || { Members: [] };
	}

	show()
	{
		this.render();
		let tmpAPI = this.pict.providers.TeamAPI;
		if (!tmpAPI || typeof tmpAPI.roster !== 'function') { return; }
		tmpAPI.roster().then((pRoster) =>
		{
			this.pict.AppData.Community.Members = Array.isArray(pRoster) ? pRoster : [];
			this._renderGrid();
		});
	}

	visit(pIDUser)
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showProfileFor === 'function') { tmpApp.showProfileFor(pIDUser); }
	}

	_renderGrid()
	{
		let tmpHost = document.getElementById('Community-Grid');
		if (!tmpHost) { return; }
		let tmpMembers = this.pict.AppData.Community.Members || [];
		if (tmpMembers.length === 0)
		{
			tmpHost.innerHTML = '<div class="cmty-empty">No one is here yet.</div>';
			return;
		}
		let tmpHTML = '';
		for (let i = 0; i < tmpMembers.length; i++)
		{
			let tmpMember = tmpMembers[i];
			// Use the same color logic as the avatar (their Color when set, else a stable color derived
			// from their name) so the stripe and avatar match and every card is colorful, set or not.
			let tmpName = tmpMember.DisplayName || ('Teammate ' + tmpMember.IDUser);
			let tmpAccent = libAvatarHelper.backgroundFor(tmpMember.Color, tmpName);
			let tmpTitle = tmpMember.Title ? ('<span class="cmty-title">' + _escapeHTML(tmpMember.Title) + '</span>') : '<span class="cmty-title cmty-title-empty">No title yet</span>';
			tmpHTML += '<button class="cmty-card" style="--cmty-accent: ' + _escapeHTML(tmpAccent) + '" onclick="_Pict.views[\'Plansheet-Community\'].visit(' + tmpMember.IDUser + ')">'
				+ '<span class="cmty-accent"></span>'
				+ '<span class="cmty-avatar">' + libAvatarHelper.avatarHTML(tmpMember, { Size: 64 }) + '</span>'
				+ '<span class="cmty-name">' + _escapeHTML(tmpName) + '</span>'
				+ tmpTitle
				+ '</button>';
		}
		tmpHost.innerHTML = tmpHTML;
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewCommunity;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Community',
	DefaultRenderable: 'Community-Display',
	AutoRender: false,
	CSS: /*css*/`
		.cmty { height: 100%; overflow: auto; }
		.cmty-head { margin-bottom: 6px; }
		.cmty-h1 { margin: 0; font-size: 1.5em; color: var(--theme-color-text-primary, #1d2230); }
		.cmty-sub { margin: 4px 0 22px; color: var(--theme-color-text-secondary, #5b6376); font-size: 0.95em; }
		.cmty-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }
		.cmty-card { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 22px 14px 18px; border: 1px solid var(--theme-color-border-default, #e7eaf0); border-radius: 14px; background: var(--theme-color-background-panel, #fff); cursor: pointer; position: relative; overflow: hidden; text-align: center; }
		.cmty-card:hover { border-color: var(--cmty-accent); box-shadow: 0 6px 22px rgba(20,30,50,0.10); transform: translateY(-2px); transition: transform 0.12s ease, box-shadow 0.12s ease; }
		.cmty-accent { position: absolute; top: 0; left: 0; right: 0; height: 6px; background: var(--cmty-accent); }
		.cmty-avatar { margin-top: 4px; }
		.cmty-name { font-weight: 600; color: var(--theme-color-text-primary, #1d2230); font-size: 0.98em; }
		.cmty-title { color: var(--theme-color-text-secondary, #5b6376); font-size: 0.82em; min-height: 1em; }
		.cmty-title-empty { opacity: 0.5; font-style: italic; }
		.cmty-empty { padding: 40px; text-align: center; color: var(--theme-color-text-secondary, #8a93a5); }
	`,
	Templates:
	[
		{
			Hash: 'Community-Display',
			Template: /*html*/`
<div class="ps-page cmty">
	<div class="cmty-head">
		<h1 class="cmty-h1">Community</h1>
		<p class="cmty-sub">Everyone on the team. Click a card to visit their page.</p>
	</div>
	<div class="cmty-grid" id="Community-Grid"></div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Community-Display',
			TemplateHash: 'Community-Display',
			ContentDestinationAddress: '#Plansheet-Workspace'
		}
	]
};
